import { useWindowDocked } from "./windowDockedContext";
import { useWindowMobileTarget } from "./windowMobileTargetContext";
import { useWindowRenderMode } from "./windowRenderModeContext";

/**
 * Pure platform detection — no state. Answers "where is this window rendering?"
 * Shared between `Window` and `SlottedWindow` so both use identical detection
 * logic. When `Window` is eventually retired and `SlottedWindow` owns the shell
 * directly, this hook stays and gains a `shellType` field for the Rnd/div branch.
 */
export function useWindowPlatformMode() {
    const renderMode = useWindowRenderMode();
    const mobileTarget = useWindowMobileTarget();
    const docked = useWindowDocked();
    return {
        /**
         * Window is inside a dock slot; height is owned by the dock layout.
         * Read from `WindowDockedContext` — the only signal `WindowSlot` actually
         * publishes. `WindowRenderMode`'s "docked" value is never provided, so
         * deriving this from render mode left it permanently false and pushed
         * docked SlottedWindows onto the floating (local-state) collapse path.
         */
        isDocked: docked,
        /**
         * Window is inside a mobile SidePanel or Drawer; react-rnd is not used.
         * Drives SlottedWindow's chrome variant (pill buttons, CaretsIn/OutVertical,
         * collapse-to-strip behaviour). Based solely on the mobile target context
         * so that WindowContentOnlyProvider used in tests does not incorrectly
         * trigger mobile chrome.
         */
        isMobile: mobileTarget !== null,
        /**
         * Window should render without the react-rnd shell.
         * True for both mobile contexts (SidePanel/Drawer) and the
         * WindowContentOnlyProvider test/legacy helper.
         * Used by the Window component for its render-path branch.
         */
        isContentOnly: renderMode === "contentOnly" || mobileTarget !== null,
        /** Mobile target is a Drawer (snap-point drag resizes; collapse button is hidden). */
        isDrawer: mobileTarget?.target === "drawer",
        /** Drawer is currently open (FAB should be visible). */
        isDrawerOpen: mobileTarget?.target === "drawer" && mobileTarget.isOpen,
        /** Side panel is currently open (FAB should be visible). */
        isSidePanelOpen: mobileTarget?.target === "sidePanel" && mobileTarget.isOpen,
    };
}
